import React from 'react'

import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import config from '~/src/App/config'
import getGlobal from '~/src/Lib/getGlobal'
import createLogger from '~/src/Lib/Logging'
import { renderableType } from '~/src/Lib/PropTypes'
import safeStorage from '~/src/Lib/safeStorage'
import { EMPTY_ARRAY, EMPTY_OBJECT } from '~/src/Lib/Utils'
import Null from '~/src/UI/Shared/Null'

const storage = safeStorage()
const notProd = process.env.NODE_ENV !== 'production' || config.ENVIRONMENT !== 'production'
const getMergedPermissions = memoizeOne((mine, test) => ({ ...mine, ...test }))
const logger = createLogger('Can')

let testPermissions
/* eslint-disable no-underscore-dangle */
if (getGlobal() && notProd) {
  getGlobal().__permissionsChecked__ = {}
  const storedPermissions = storage.getItem('testPermissions')
  if (storedPermissions) {
    try {
      testPermissions = JSON.parse(storedPermissions)
    } catch (error) {
      console.warn(error)
    }
  }
}
/* eslint-enable no-underscore-dangle */

export const permitted = (myPermissions = EMPTY_OBJECT, actions = EMPTY_ARRAY, any = false) => {
  let permissions = myPermissions
  let pageChecked
  if (notProd) {
    const { __permissionsChecked__, location = EMPTY_OBJECT } = getGlobal() ?? EMPTY_OBJECT
    pageChecked = __permissionsChecked__ && (
      __permissionsChecked__[location.pathname] ?? (__permissionsChecked__[location.pathname] = {})
    )
    if (testPermissions) {
      permissions = getMergedPermissions(myPermissions, testPermissions)
    }
  }
  const requiredPermissions = Array.isArray(actions) ? actions : Array.of(actions)
  logger.debug({ effectivePermissions: permissions, requiredPermissions, any })
  return requiredPermissions[any ? 'some' : 'every'](action => {
    const can = typeof action === 'function' ? action(permissions) : Boolean(permissions?.[action])
    if (pageChecked && action) {
      pageChecked[typeof action === 'function' ? action?.name : action] = can
    }

    return can
  })
}
/* eslint-enable babel/camelcase, no-underscore-dangle */

export const usePermissions = () => {
  const { myPermissions } = useConnect('selectMyPermissions')
  if (notProd && testPermissions) {
    return getMergedPermissions(myPermissions, testPermissions)
  }
  return myPermissions
}

export const useCan = (actions, any) => {
  const { myPermissions } = useConnect('selectMyPermissions')
  if (!actions.length) return true
  return permitted(myPermissions, actions, any)
}

export const CanComponent = ({
  can,
  children = null,
  Fallback = Null,
  FallbackProps = EMPTY_OBJECT
}) => {
  if (can) return children
  return React.isValidElement(Fallback) ? Fallback : <Fallback {...FallbackProps} />
}

CanComponent.propTypes = {
  can: PropTypes.bool.isRequired,
  children: PropTypes.node,
  Fallback: PropTypes.oneOfType([renderableType, PropTypes.element]),
  // eslint-disable-next-line react/forbid-prop-types
  FallbackProps: PropTypes.object,
}

CanComponent.displayName = 'Can'

const Memoized = React.memo(({
  actions = EMPTY_ARRAY,
  any = false,
  ...passthru
}) => {
  const can = useCan(actions, any)

  return <CanComponent {...passthru} can={can} />
})
Memoized.propTypes = {
  actions: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.func,
    ])),
  ]),
  any: PropTypes.bool,
  children: PropTypes.node.isRequired,
}
Memoized.displayName = `Memoized(${CanComponent.displayName})`

export default Memoized
